'use client'

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { pushToDataLayer, withPageContext } from '@/app/lib/analytics/dataLayer'
import type {
  ClickEventParams,
  PageContext,
  PriceRedactedSelectItemParams,
  SearchEventParams,
  ViewItemListParams,
} from '@/app/lib/analytics/types'

interface ChromeAnalyticsValue {
  /** Usado por `PageAnalyticsProvider` para reportar la página activa hacia arriba. */
  readonly setPageContext: (context: PageContext) => void
  /** Empuja un `click` con el contexto de la página activa. No-op si aún no se ha reportado ninguno. */
  readonly trackClick: (params: ClickEventParams) => void
  /** Empuja un `search` con el contexto de la página activa. No-op si aún no se ha reportado ninguno. */
  readonly trackSearch: (params: SearchEventParams) => void
  /** Empuja un `view_item_list` con el contexto de la página activa. No-op si aún no se ha reportado ninguno. */
  readonly trackViewItemList: (params: ViewItemListParams) => void
  /**
   * Empuja un `select_item` con el contexto de la página activa. No-op si aún
   * no se ha reportado ninguno. Tipado con `PriceRedactedSelectItemParams`
   * (no el `SelectItemParams` sin redactar de `PageAnalyticsContext`): hoy el
   * único consumidor es "Productos más buscados" (`SearchInput`), cuyo CSV
   * redacta `price` a `no_aplica` — generalizar este tipo si un segundo
   * módulo del chrome necesitara un `select_item` sin redactar.
   */
  readonly trackSelectItem: (params: PriceRedactedSelectItemParams) => void
}

const ChromeAnalyticsContext = createContext<ChromeAnalyticsValue | null>(null)

/**
 * Da contexto de página al chrome (header/footer): en `entradas/(general-web)/layout.tsx`,
 * `SiteHeader`/`SiteFooter` son HERMANOS de `<main>{children}</main>`, no descendientes de
 * ningún `PageAnalyticsProvider` de página — sus hojas (`SearchInput`, `NavDrawer`, `FooterClickTracker`, etc.) recibían
 * `null` de `useOptionalPageAnalytics()` de forma silenciosa y ningún evento se emitía
 * (ver `specs/`/memoria de proyecto "Chrome fuera del PageAnalyticsProvider").
 *
 * Este provider se monta una sola vez en el layout raíz, envolviendo header + `main` +
 * footer. Cada `PageAnalyticsProvider` de página (dentro de `{children}`) reporta su
 * `context` aquí vía `setPageContext` al montar/cambiar de página.
 *
 * El contexto vive en un `useRef`, no en `useState`: ninguna hoja del chrome
 * (`SearchInput`, footer, etc.) renderiza nada en función de él — solo lo necesitan de
 * forma imperativa dentro de sus `onClick` (`trackClick`/`trackSearch`). Un
 * `useState` dispararía un re-render de cada consumidor en cada navegación de
 * página sin ningún beneficio; con `ref` el reporte de contexto no re-renderiza
 * nada y `value` queda estable para siempre.
 */
export function ChromeAnalyticsProvider({ children }: { children: ReactNode }) {
  const contextRef = useRef<PageContext | null>(null)

  const setPageContext = useCallback((context: PageContext) => {
    contextRef.current = context
  }, [])

  const trackClick = useCallback((params: ClickEventParams) => {
    const context = contextRef.current
    if (!context) return
    pushToDataLayer(withPageContext(context, { event: 'click', ...params }))
  }, [])

  const trackSearch = useCallback((params: SearchEventParams) => {
    const context = contextRef.current
    if (!context) return
    pushToDataLayer(withPageContext(context, { event: 'search', ...params }))
  }, [])

  const trackViewItemList = useCallback((params: ViewItemListParams) => {
    const context = contextRef.current
    if (!context) return
    pushToDataLayer(withPageContext(context, { event: 'view_item_list', ...params }))
  }, [])

  const trackSelectItem = useCallback((params: PriceRedactedSelectItemParams) => {
    const context = contextRef.current
    if (!context) return
    pushToDataLayer(withPageContext(context, { event: 'select_item', ...params }))
  }, [])

  const value = useMemo<ChromeAnalyticsValue>(
    () => ({ setPageContext, trackClick, trackSearch, trackViewItemList, trackSelectItem }),
    [setPageContext, trackClick, trackSearch, trackViewItemList, trackSelectItem],
  )

  return <ChromeAnalyticsContext.Provider value={value}>{children}</ChromeAnalyticsContext.Provider>
}

/**
 * Tolerante: devuelve `null` fuera del provider (tests, `(canales)` layout, que
 * todavía no lo monta). Para hojas del chrome reutilizables, igual que
 * `useOptionalPageAnalytics`.
 */
export function useOptionalChromeAnalytics(): ChromeAnalyticsValue | null {
  return useContext(ChromeAnalyticsContext)
}
